import { state } from "@wb/ui/analytics/state";
import { track } from "@wb/ui/analytics/track";
import { useEffect, useRef } from "react";

interface TrackableElement extends HTMLElement {
  hasTracking?: boolean;
}

type EventListener = (e: Event) => void;

/**
 * Hook that automatically tracks click events on elements with data-event attributes.
 * Uses MutationObserver to detect new elements and WeakMap to store event listeners.
 * @returns void
 */
export function useTrack() {
  const { enabled } = state;
  const listenersMap = useRef(new WeakMap<HTMLElement, EventListener>());

  useEffect(() => {
    if (!enabled) return;

    const handleClick = (element: TrackableElement) => {
      return () => {
        try {
          const event = element.getAttribute("data-event");
          if (event) track(event);
        } catch (error) {
          console.error("Failed to track event:", error);
        }
      };
    };

    const attachListener = (element: TrackableElement) => {
      if (element.hasTracking) return;

      const eventName = element.getAttribute("data-event");
      if (!eventName) return;

      const listener = handleClick(element);
      listenersMap.current.set(element, listener);
      element.addEventListener("click", listener);
      element.hasTracking = true;
    };

    const processNode = (node: Node) => {
      if (!(node instanceof HTMLElement)) return;

      // Check the node itself
      if (node.hasAttribute("data-event"))
        attachListener(node as TrackableElement);

      // Check child elements
      [...node.querySelectorAll("[data-event]")].map((element) => {
        attachListener(element as TrackableElement);
      });
    };

    const observer = new MutationObserver((mutations) => {
      mutations.map((mutation) => {
        mutation.addedNodes.forEach(processNode);
      });
    });

    // Process existing elements
    processNode(document.body);

    // Observe future changes
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
      // Clean up all event listeners by iterating through entries
      for (const element of document.querySelectorAll("[data-event]")) {
        const listener = listenersMap.current.get(element as HTMLElement);
        if (listener) {
          element.removeEventListener("click", listener);
          listenersMap.current.delete(element as HTMLElement);
        }
      }
    };
  }, [enabled]);
}
